import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Payment from '../components/Payment';
import { Pricing } from '../components/Pricing';
import { useAuth } from '../context/AuthContext';
import { planOptions } from '../components/Pricing';
import { useCSRF } from '../utils/csrfUtils';
import { useNavigate } from 'react-router-dom';

const PricingPage = () => {
    const navigate = useNavigate();
    const { pgUser, sessionToken, refreshPgUser } = useAuth();
    const [isAnnual, setIsAnnual] = useState(false);
    const [showPayment, setShowPayment] = useState(false);
    const [confirmedPlan, setConfirmedPlan] = useState(null);
    const [betaCode, setBetaCode] = useState('');
    const [betaCodeMessage, setBetaCodeMessage] = useState('');
    const [applyingBetaCode, setApplyingBetaCode] = useState(false);
    const [joiningWaitlist, setJoiningWaitlist] = useState(false);
    const paymentRef = useRef(null);
    const { fetchWithCSRF, isLoading: csrfLoading } = useCSRF();

    // Check if user is already on waitlist
    useEffect(() => {
        const checkWaitlistStatus = async () => {
            if (pgUser?.email) {
                try {
                    const response = await fetch(`/api/v1/waitlist/check?email=${encodeURIComponent(pgUser.email)}`);
                    const data = await response.json();
                    if (data.onWaitlist) {
                        navigate('/waitlist', { state: { alreadyOnWaitlist: true } });
                    }
                } catch (error) {
                    console.error('Error checking waitlist status:', error);
                }
            }
        };
        checkWaitlistStatus();
    }, [pgUser, navigate]);


    const selectedPlan = {
        id: isAnnual ? 'price_yearly' : 'price_monthly',
        name: isAnnual ? 'Annual Plan' : 'Monthly Plan',
        price: isAnnual ? '$48/year' : '$5/month'
    };

    const handlePlanSelection = () => {
        setConfirmedPlan({...selectedPlan});
        setShowPayment(true);
    };

    const handleApplyBetaCode = async (betaCode) => {
        setApplyingBetaCode(true);
        try {
            console.log("checking beta code with session token: " + sessionToken);
            const response = await fetchWithCSRF(`/api/v1/beta-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ code: betaCode })
            });

            // Parse the response body once
            let data;
            try {
                data = await response.json();
            } catch (parseError) {
                console.error('Error parsing response:', parseError);
                setBetaCodeMessage("An error occurred. Please try again later.");
                return;
            }

            if (!response.ok) {
                if (response.status === 403) {
                    setBetaCodeMessage("Session expired. Please refresh the page and try again.");
                    return;
                }
                if (response.status === 404) {
                    console.log("Error response data:", data);
                    if (data.message === "Invalid code.") {
                        setBetaCodeMessage("Invalid beta code");
                    } else if (data.message === "Code already claimed.") {
                        setBetaCodeMessage("Beta code already claimed");
                    } else {
                        setBetaCodeMessage("An error occurred. Please try again later.");
                    }
                    return;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            if (data.exists && !data.available) {
                setBetaCodeMessage("Beta code already claimed");
            } else if (data.exists && data.available) {
                setBetaCodeMessage("Beta code applied successfully");
                // Refresh pgUser to get updated has_access value before navigating
                await refreshPgUser();
                navigate('/home');
            } else {
                setBetaCodeMessage("Invalid beta code");
            }
        } catch (error) {

            console.error('Error applying beta code:', error);
            setBetaCodeMessage("An error occurred. Please try again later.");
        } finally {
            setApplyingBetaCode(false);
        }
    };

    const handleJoinWaitlist = async () => {
        if (!pgUser?.email) {
            setBetaCodeMessage("Please log in to join the waitlist.");
            return;
        }

        setJoiningWaitlist(true);
        setBetaCodeMessage('');

        try {
            const response = await fetchWithCSRF('/api/v1/waitlist', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ email: pgUser.email }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.alreadyExists) {
                    // Already on waitlist, just redirect
                    navigate('/waitlist');
                    return;
                }
                setBetaCodeMessage(data.error || 'Something went wrong. Please try again.');
                return;
            }

            navigate('/waitlist');
        } catch (error) {
            console.error('Error joining waitlist:', error);
            setBetaCodeMessage('Something went wrong. Please try again.');
        } finally {
            setJoiningWaitlist(false);
        }
    };

    useEffect(() => {
        if (showPayment && paymentRef.current) {
            paymentRef.current.scrollIntoView({ 
                behavior: 'smooth',
                block: 'center'
            });
        }
    }, [showPayment]);

    const planOptions = [
        {
            link: 
                process.env.NODE_ENV === 'development' ? 'https://buy.stripe.com/test_bIY9CLfZUcMOadO000' : '',
                priceId:
                    process.env.NODE_ENV === 'development' ? 'price_1QzNTRIomkyxBjboqLPQd1pC' : '',
                    price: 5,
                    duration: '/month',
        },
        {
            link: 
                process.env.NODE_ENV === 'development' ? 'https://buy.stripe.com/test_4gwcOXbJEeUW99K5kl' : '',
                priceId:
                    process.env.NODE_ENV === 'development' ? 'price_1QzNaYIomkyxBjbo58sWA90K' : '',
                    price: 48,
                    duration: '/year',
        },
        {
            link: 
                process.env.NODE_ENV === 'production' ? 'https://buy.stripe.com/test_bIY9CLfZUcMOadO000' : '',
                priceId:
                    process.env.NODE_ENV === 'production' ? 'price_1QzNTRIomkyxBjboqLPQd1pC' : '',
                    price: 5,
                    duration: '/month',
        },
        {
            link: 
                process.env.NODE_ENV === 'production' ? 'https://buy.stripe.com/test_4gwcOXbJEeUW99K5kl' : '', //TODO: Change to production link
                priceId:
                    process.env.NODE_ENV === 'production' ? 'price_1QzNaYIomkyxBjbo58sWA90K' : '',
                    price: 48,
                    duration: '/year',
        }

        
    ]

    return (
       


        <>
            <div className="landing-header" style={{ position: 'relative' }}>
                <Link to="/pricing" className="pricing-brand">
                    <img src="images/autochef_logo.svg" alt="" className="company-logo" />
                    <span className="company-name" style={{ color: '#000000' }}>AutoChef</span>
                </Link>
            </div>

            <Pricing />
            {/* <Container className="py-3">
                <Row className="justify-content-center text-center mb-4">
                    <Col md={8}>
                        <h1 className="mb-4">Choose Your Plan</h1>
                        <p className="lead">Get cooking with AutoChef today and transform your meal planning experience.</p>
                        
                        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                            <Button 
                                className={`plan-select-btn ${!isAnnual ? 'active' : ''}`}
                                onClick={() => setIsAnnual(false)}
                            >
                                Monthly
                            </Button>
                            <Button 
                                className={`plan-select-btn ${isAnnual ? 'active' : ''}`}
                                onClick={() => setIsAnnual(true)}
                            >
                                Annual
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Row className="justify-content-center align-items-start">
                    <Col lg={6} className="mb-4">
                        <Card className="h-100 shadow pricing-card">
                            <Card.Body className="d-flex flex-column">
                            
                                <Card.Title className="text-center mb-4">
                                    <h2>{isAnnual ? 'Annual' : 'Monthly'}</h2>
                                    <div className="display-4">${isAnnual ? '4' : '5'}</div>
                                    <div className="text-muted">per month</div>
                                    {isAnnual && (
                                        <div className="text-success small">Billed annually at $48/year</div>
                                    )}
                                    {!isAnnual && (
                                        <div className="text-success small">Billed monthly at $5/month  </div>
                                    )}
                                </Card.Title>
                                <Card.Text>
                                    <ul className="list-unstyled">
                                        <li className="mb-3">Personalized meal plans every week</li>
                                        <li className="mb-3">Unlimited meal plan regenerations</li>
                                        <li className="mb-3">Smart grocery lists</li>
                                        <li className="mb-3">Pantry management</li>
                                        <li className="mb-3">One-click grocery orders (coming soon)</li>
                                        
                                    </ul>
                                </Card.Text>
                                <div className="mt-auto">
                                    {(() => {
                                        const isProduction = process.env.NODE_ENV === 'production';
                                        const selectedOption = planOptions[isAnnual ? (isProduction ? 3 : 1) : (isProduction ? 2 : 0)];
                                        const baseLink = selectedOption.link;
                                        const email = pgUser?.email;

                                        // Only construct URL if we have both link and email
                                        const finalUrl = baseLink && email ? `${baseLink}?prefilled_email=${email}` : '#';

                                        return (
                                            <button
                                                className="btn btn-primary w-100"
                                                onClick={() => {
                                                    if (finalUrl !== '#' && baseLink && email) {
                                                        window.open(finalUrl, '_blank', 'noopener,noreferrer');
                                                    } else {
                                                        console.warn('Cannot open Stripe link: missing data', { baseLink, email });
                                                    }
                                                }}
                                                // disabled={!baseLink || !email}
                                                disabled={true}
                                            >
                                                {(!baseLink || !email) ? 'Loading...' : 'Select Plan'}
                                            </button>
                                        );
                                    })()}
                                </div>  
                            </Card.Body>
                        </Card>
                    </Col>
                    
                </Row>
            </Container> */}
            <Container className="py-3">
                <Row className="justify-content-center text-center mb-4">
                    <Col md={8}>
                    <h4>AutoChef is currently in a closed beta. Enter your code below to get access.</h4>
                    {/* <p className="lead mb-1 ">Have a beta code? Enter it below to get access to the app.</p> */}
                        <input 
                            type="text" 
                            className="form-control form-control-custom" 
                            placeholder="Enter beta code" 
                            value={betaCode}
                            onChange={(e) => setBetaCode(e.target.value)}
                            style={{ width: '200px', margin: '0 auto' }} 
                        />
                        <Button
                            className="btn mt-3"
                            onClick={() => handleApplyBetaCode(betaCode)}
                            disabled={!betaCode.trim() || applyingBetaCode}
                            style={
                                (!betaCode.trim() || applyingBetaCode)
                                    ? { borderColor: '#52cd92 !important' }
                                    : undefined
                            }
                        >
                            {applyingBetaCode ? (
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    style={{ color: 'white' }}
                                />
                            ) : (
                                'Apply Code'
                            )}
                        </Button>
                        <div className="mt-2">
                            <span 
                                onClick={handleJoinWaitlist}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                                {joiningWaitlist ? 'Joining...' : 'Join Waitlist'}
                            </span>
                        </div>
                        {betaCodeMessage && (
                            <p className="mt-2 text-danger">{betaCodeMessage}</p>
                        )}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default PricingPage; 
